import { useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useStatusOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useUser } from '@fs/zion-user'
import { usePermission } from '@fs/zion-permissions'
import useReactQuery from './useReactQuery'
import {
  getDisplayNotificationPrefs,
  updateNotificationPref,
  DISPLAY_PROFILE,
} from '../services/notificationPrefsService'

const byDisplayOrder = (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)

// Returns a copy of the cached display-prefs payload with one channel's subscription set to `optedIn`.
// Lives at module scope so the optimistic update below doesn't nest arrow functions five deep.
const withUpdatedChannel = (old, { categoryId, channel, optedIn }) => {
  if (!old?.data?.categories) return old
  return {
    ...old,
    data: {
      ...old.data,
      categories: old.data.categories.map((category) =>
        category.categoryId !== categoryId
          ? category
          : {
              ...category,
              channels: category.channels.map((ch) => (ch.channel === channel ? { ...ch, subscribed: optedIn } : ch)),
            }
      ),
    },
  }
}

/**
 * Reads the Together notification preferences from NPS endpoint for the signed-in user
 * (member vs. non-member profile) and exposes an optimistic toggle mutation.
 *
 * `enabled` defaults to `false` on purpose. The NPS endpoints are only deployed to beta/integration
 * and the screen that uses them sits behind the `discovery_together_notificationPreferences` flag, so
 * callers must opt in explicitly. Failing closed means a caller that forgets the gate renders empty
 * rather than firing requests at endpoints that don't exist in prod.
 *
 * @param {object} [options]
 * @param {boolean} [options.enabled=false] Whether the preferences query may run.
 */
export default function useNotificationPrefs({ enabled = false } = {}) {
  const { t } = useTranslation()
  const { signedIn } = useUser()
  const [isMember] = usePermission('MemberContentPermission', 'DiscoveryUserContext')
  const showStatusOverlay = useStatusOverlay()
  const queryClient = useQueryClient()

  const profile = isMember ? DISPLAY_PROFILE.member : DISPLAY_PROFILE.default
  const queryKey = ['notificationPrefs', profile]

  const { query, data } = useReactQuery({
    queryKey,
    queryFn: () => getDisplayNotificationPrefs(profile),
    enabled: enabled && !!signedIn,
  })

  const categories = useMemo(() => {
    const list = data?.categories ?? []
    return [...list].sort(byDisplayOrder).map((category) => ({
      ...category,
      channels: [...(category.channels ?? [])].sort(byDisplayOrder),
    }))
  }, [data])

  const { mutate: updatePref } = useMutation({
    mutationFn: updateNotificationPref,
    // Optimistically flip the toggle so the change feels instant
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData(queryKey)
      queryClient.setQueryData(queryKey, (old) => withUpdatedChannel(old, variables))
      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous)
      }
      showStatusOverlay({ message: t('error.generic', 'Oops. Something went wrong.'), type: 'error' })
    },
    // Re-sync with the server whether the write succeeded or failed.
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })

  return {
    categories,
    isLoading: query.isLoading,
    isError: query.isError,
    updatePref,
  }
}
